import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import { shouldHideStampedFoilImage } from '@zola-helpers/client/dist/es/util/cards';
import getZolaImageFromUuid from '@zola-helpers/client/dist/es/util/getZolaImageFromUuid';
import type { FileInfo } from '@uploadcare/react-widget';

import { STAMPED_FOIL_IMAGE_TAGS } from '@zola-helpers/client/dist/es/constants/cards';
import { ptToPx } from '../../../../util/font/pointPixelConversion';
import { usePhotoObjectURL } from '../../../hooks/usePhotoObjectURL';
import { Bleed, DEFAULT_CARD_BLEED } from '../../util/getCardBleed';
import type { ImageElementView } from '../../types/ElementView';
import styles from './imageElement.module.less';

/** Generates a CSS url string by adding a width query param if necessary " */
const getUrlString = (baseUrl: string | null, targetWidthPts: number, elementWidthPts: number) => {
  // Safety: If no base image URL, abort immediately.
  if (!baseUrl) return '';

  // If the target width is the default element width, no need to resize
  if (targetWidthPts === elementWidthPts) return `url(${baseUrl})`;

  // Otherwise, convert target with to px and add width query param to URL
  const targetWidthPx = Math.round(ptToPx(targetWidthPts));
  return `url(${baseUrl}?w=${targetWidthPx})`;
};

type ImageElementProps = {
  element: ImageElementView;
  scale?: number;
  bleed?: Bleed;
  border?: boolean;
  pixelCrop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  photo?: {
    valid: boolean;
    filledSize: number;
    file: File;
    displayedBlob: Blob;
  };
  uploadcareFile?: FileInfo;
  className?: string;
  isRounded?: boolean;
  isFolded?: boolean;
  foilColor?: string;
};

export const ImageElement = ({
  element,
  scale = 1,
  bleed,
  border = false,
  pixelCrop = undefined,
  photo = undefined,
  uploadcareFile = undefined,
  className = undefined,
  isRounded = false,
  isFolded = false,
  foilColor = 'gold',
}: ImageElementProps) => {
  const photoURL = usePhotoObjectURL(photo);
  const shouldHide = shouldHideStampedFoilImage(
    element?.tag,
    foilColor as keyof typeof STAMPED_FOIL_IMAGE_TAGS
  );

  // Allows us to use smaller assets, yet avoid refetching when larger assets are already available.
  const effectiveWidth = Math.round((element?.width || 0) * Math.min(scale, 1));
  const [maxFetchedWidth, setMaxFetchedWidth] = useState<number>(effectiveWidth);
  useEffect(() => {
    // If the effective width grows, update the max (which will load a bigger asset)
    if (effectiveWidth > maxFetchedWidth) {
      setMaxFetchedWidth(effectiveWidth);
    }
  }, [effectiveWidth, maxFetchedWidth]);

  if (!element || shouldHide) {
    return null;
  }

  const { customizable, overlay, layer = 0 } = element;
  const isFoldedCardElement = isFolded && !customizable && !overlay;

  const elemHight = isFoldedCardElement ? element.height / 2 : element.height;

  /*
   * Most card templates include the bleed (area cut off after printing) in their dimensions.
   * To accurately show what the printed result will be, we subtract the bleed value from our
   * preview dimensions, and must therefore adjust element positions by the same amount.
   */
  const leftBleed = bleed ? bleed.left : DEFAULT_CARD_BLEED;
  const topBleed = bleed ? bleed.top : DEFAULT_CARD_BLEED;
  const elementLeftPos = element.x - leftBleed;
  const elementTopPos = (isFoldedCardElement ? elemHight : element.y) - topBleed; // Only landscape folded are supported for now

  const divStyle = {
    zIndex: layer,
    left: `${elementLeftPos}pt`,
    top: `${elementTopPos}pt`,
    width: `${element.width}pt`,
    height: `${elemHight}pt`,
  };

  let imgURL;
  const croppedImageStyle: {
    backgroundPosition?: string;
    backgroundSize?: string;
    backgroundImage?: string;
  } = {};

  if (uploadcareFile) {
    // Uploadcare image
    imgURL = uploadcareFile.cdnUrl;
  } else if (photo && photo.valid && pixelCrop && photoURL) {
    // Blob image
    const { filledSize } = photo;

    const backgroundScale = {
      w: ptToPx(element.width) / filledSize,
      h: ptToPx(elemHight) / filledSize,
    };

    const cropScale = { w: filledSize / pixelCrop.width, h: filledSize / pixelCrop.height };

    const offset = {
      x: -pixelCrop.x * backgroundScale.w * cropScale.w,
      y: -pixelCrop.y * backgroundScale.h * cropScale.h,
    };

    croppedImageStyle.backgroundPosition = `${offset.x}px ${offset.y}px`;
    croppedImageStyle.backgroundSize = `${Math.round(100 * cropScale.w)}%`;

    imgURL = photoURL;
  } else {
    // Default element image
    imgURL = getZolaImageFromUuid(element.image_id);
  }

  let backgroundImage;
  if (maxFetchedWidth) {
    // Default (non-retina) asset url. Width param omitted if equal to full element width.
    const width1x = maxFetchedWidth;
    const url1x = getUrlString(imgURL, width1x, element.width);

    // 2x asset url. Never wider than the element itself, and only used if larger than the 1x width.
    const width2x = Math.min(element.width, width1x * 2);
    const url2x = width2x > width1x ? getUrlString(imgURL, width2x, element.width) : '';

    backgroundImage = url2x ? `image-set(${url1x} 1x, ${url2x} 2x)` : url1x;
  }

  croppedImageStyle.backgroundImage = backgroundImage;
  croppedImageStyle.backgroundPosition = isFoldedCardElement ? 'bottom' : '';

  return (
    <div
      className={cx(styles.imageLayer, className, {
        [styles.rounded]: isRounded,
        [styles.border]: border,
      })}
      style={{ ...divStyle, ...croppedImageStyle }}
    />
  );
};
